import axios from '@axios'

const route = 'manager_room'

const normalizeBoolean = value => value === true || value === 1 || value === '1' || value === 'yes'

const isWithIssEnabled = value => value === true || value === 1 || value === '1' || value === 'yes' || value === 'true' || value === 'sim'

const toNumber = value => {
  const parsed = parseFloat(value)

  return Number.isNaN(parsed) ? 0 : parsed
}

const shouldIncludeByScope = (scope, origin) => {
  if (scope === 'total') {
    return true
  }
  if (scope === 'accommodation') {
    return origin === 'accommodation' || origin === 'extra_tarif'
  }
  if (scope === 'consumption') {
    return origin === 'product' || origin === 'service'
  }

  return true
}

const getBillingContext = room => {
  const accommodation = room && room.current_accommodation ? room.current_accommodation : null
  const reserve = (accommodation && accommodation.reserve)
    || (room && room.current_reserve)
    || accommodation
  const company = (accommodation && accommodation.company) || (reserve && reserve.company) || null

  return {
    reserve,
    company,
  }
}

const calculateBillingScopeTotal = ({
  checkoutItems, scope, withIss, issRateDecimal,
}) => checkoutItems.reduce((acc, item) => {
  if (!shouldIncludeByScope(scope, item.origin)) {
    return acc
  }

  const quantity = toNumber(item?.pivot?.quantity ?? 1)
  const salePrice = toNumber(item.sale_price)
  const baseValue = salePrice * quantity
  let taxValue = 0

  if (item.origin === 'service') {
    taxValue = (toNumber(item.iss) / 100) * salePrice * quantity
  }

  if (item.origin === 'accommodation' && withIss) {
    taxValue += toNumber(issRateDecimal) * salePrice * quantity
  }

  return acc + baseValue + taxValue
}, 0)

const applyAutoBillingInvoice = state => {
  // Corporate billing is settled only in the final checkout, never in advances.
  if (state.originPartialPayment !== 'checkout' || state.autoBillingApplied) {
    return
  }

  const { reserve, company } = getBillingContext(state.room)
  if (!reserve || !normalizeBoolean(reserve.billing_checkout_enabled) || !company || !normalizeBoolean(company.allow_billing)) {
    return
  }

  if (!Array.isArray(state.checkout) || state.checkout.length === 0) {
    return
  }

  const hasInvoice = state.paymentMethods.some(payment => payment.method === 'invoice')
  if (hasInvoice) {
    state.autoBillingApplied = true

    return
  }

  const scope = reserve.billing_checkout_scope || 'total'
  const withIss = isWithIssEnabled(state.room?.current_accommodation?.with_iss)
  const issRate = 0.03
  const invoiceValue = calculateBillingScopeTotal({
    checkoutItems: state.checkout,
    scope,
    withIss,
    issRateDecimal: issRate,
  })

  if (invoiceValue <= 0) {
    return
  }

  state.paymentMethods.push({
    method: 'invoice',
    value: invoiceValue,
    billing_scope: scope,
    auto_billing: true,
    origin: 'company',
    id: company.id,
  })
  state.autoBillingApplied = true
}

export default {
  namespaced: true,
  state: {
    room: {},
    checkout: [],
    stage: 'new_sale',
    paymentMethods: [],
    paymentMethodsPartial: [],
    note: '',
    additional: { value: 0, additional_obs: '' },
    discount: { value: 0, discount_obs: '' },
    selectResponsible: {},
    originPartialPayment: 'checkin',
    dataNotaFiscal: {},
    separate_payment: false,
    autoBillingApplied: false,
  },

  getters: {
    getPaymentAttach(state) {
      if (state.room.current_accommodation.payment_attachment) {
        return state.room.current_accommodation.payment_attachment.id
      }

      return null
    },
    getPaymentMethodsPartial(state) {
      return state.paymentMethodsPartial
    },

    getPaymentMethodsPartialTotal(state) {
      // soma todos os produtos
      return state.paymentMethodsPartial.reduce((accumalator, item) => accumalator + parseFloat(item.value), 0)
    },

    getOriginPartialPayment(state) {
      return state.originPartialPayment
    },

    getAccommodationValue(state) {
      const { check_in } = state.room.current_accommodation
      const { check_out } = state.room.current_accommodation
      let tariff = 0
      if (state.room.current_accommodation.tariff_manual == 'not') {
        tariff = state.room.current_accommodation.tariff.value
      } else {
        tariff = state.room.current_accommodation.tariff_manual_value
      }

      const diffInMs = new Date(check_out) - new Date(check_in)
      const days = diffInMs / (1000 * 60 * 60 * 24)

      const total = parseFloat(days) * parseFloat(tariff)

      return {
        days,
        tariff,
        total,
      }
    },

    getAdditional(state) {
      return state.additional
    },
    getDiscount(state) {
      return state.discount
    },
    getRoom(state) {
      return state.room
    },
    getItemsCheckout(state) {
      return state.checkout
    },
    getNote(state) {
      return state.note
    },
    getCustomer(state) {
      return state.customer
    },
    getStage(state) {
      return state.stage
    },
    getSubtotal(state) {
      // soma todos os produtos
      return state.checkout.reduce((accumalator, item) => accumalator + (item.sale_price * item.pivot.quantity), 0)
    },
    getChange(state, getters) {
      const totalReceive = state.paymentMethods.reduce((acc, method) => acc + parseFloat(method.value), 0)

      const sumPaymentPartial = state.paymentMethodsPartial.reduce((sum, payment) => sum + parseFloat(payment.value), 0)

      const result = (parseFloat(totalReceive) + parseFloat(sumPaymentPartial)) - parseFloat(getters.getTotalToPay)
      if (result < 0) {
        return 0
      }

      return result
    },

    // total do checkout
    getTotalToPay(state, getters) {
      return (getters.getTaxation // impostos
         + parseFloat(getters.getSubtotal) // subtotal produtos /servicos
         + parseFloat(state.additional.value)) // valor adicional
         - (parseFloat(state.discount.value)) // valor desconto + ADIANTAMENTO
    },

    // total a pagar
    getToPay(state, getters) {
      const { getTotalToPay } = getters
      const partialTotal = parseFloat(getters.getPaymentMethodsPartialTotal)
      const { getPaymentMethodsTotal } = getters

      return getTotalToPay - (partialTotal + getPaymentMethodsTotal)
    },
    getpaymentMethods(state) {
      return state.paymentMethods
    },

    getPaymentMethodsTotal(state) {
      return state.paymentMethods.reduce((acc, method) => acc + parseFloat(method.value), 0)
    },

    remainingValue(state, getters) {
      let sumPayment = 0
      let sumPaymentPartial = 0
      sumPayment = state.paymentMethods.reduce((sum, payment) => sum + parseFloat(payment.value), 0)
      sumPaymentPartial = parseFloat(getters.getPaymentMethodsPartialTotal)

      return getters.getTotalToPay - (sumPayment + sumPaymentPartial)
    },

    activeBtnCheckout(state, getters) {
      return Math.round(getters.remainingValue) // arredonda mas o numero deve ser toa pequeno q sera igua a zero
    },
    getTaxation(state, getters, rootState, rootGetters) {
      // Somatorio de imposto ISS por serviço
      const iss = state.checkout.reduce((acc, item) => {
        if (item.origin == 'service') {
          // ex : ((3% /100) * valor de venda) * quantidade
          return acc + ((parseFloat(item.iss) / 100) * parseFloat(item.sale_price)) * parseFloat(item.pivot.quantity)
        }

        return acc
      }, 0)

      // Somatorio de imposto ISS por hospedagem
      let iss_accommodation = 0

      if (isWithIssEnabled(state.room.current_accommodation.with_iss)) {
        const issRate = rootGetters['config/getIssRate'] || 0.03
        iss_accommodation = state.checkout.reduce((acc, item) => {
          if (item.origin == 'accommodation') {
            // ex : 3% * hospedage
            return acc + (issRate * parseFloat(item.sale_price))
          }

          return acc
        }, 0)
      }

      return iss + iss_accommodation
    },

    getSelectecResposible(state) {
      return state.selectResponsible
    },

    // PAGAMENTO SEPARADO
    remainingValueSeparate(state, getters) {
      const responsible = state.selectResponsible

      // soma todas as formas de pagamentos do responsavel
      let sumPayment = 0
      sumPayment = state.paymentMethods.reduce((sum, payment) => {
        if (payment.origin == responsible.origin && payment.id == responsible.id) {
          return sum + parseFloat(payment.value)
        }

        return sum
      }, 0)

      const sumPaymentPartial = state.paymentMethodsPartial.reduce((sum, payment) => {
        if (payment.origin == responsible.origin && payment.id == responsible.id) {
          return sum + parseFloat(payment.value)
        }

        return sum
      }, 0)

      return getters.getTotalToPaySeparate - (sumPayment + sumPaymentPartial)
    },
    getTotalToPaySeparate(state, getters) {
      return getters.getSubTotalSeparateWithTax
    },
    getSubTotalSeparateWithTax(state, getters, rootState, rootGetters) {
      const withIss = isWithIssEnabled(state.room.current_accommodation.with_iss)
      const issRate = rootGetters['config/getIssRate'] || 0.03
      const responsible = state.selectResponsible
      const sum = getters.getItemsCheckout.reduce((acc, item) => {
        if (item.responsible) { // se tem responsavel pelo pagamento
          // verifica a origin e o id dos item com o responsavel
          if (item.responsible.origin == responsible.origin && item.responsible.id == responsible.id) {
            let iss = 0
            let iss_accommodation = 0
            if (item.origin == 'service') {
              iss = ((parseFloat(item.iss) / 100) * parseFloat(item.sale_price)) * parseFloat(item.pivot.quantity)
            }

            // iss de hospedagem
            if (item.origin == 'accommodation' && withIss) {
              iss_accommodation = issRate * parseFloat(item.sale_price)
            }

            return acc + (parseFloat(item.sale_price) * parseFloat(item.pivot.quantity)) + iss + iss_accommodation
          }
        }

        return acc
      }, 0)

      return sum
    },

    getpaymentMethodsSeparate(state) {
      const partialMethods = state.paymentMethodsPartial.map(payment => ({
        ...payment,
        is_partial: true,
      }))

      return partialMethods.concat(state.paymentMethods)
    },

  },
  mutations: {
    // seta dados nota fiscal
    setDataNotaFiscal(state, dataNotafiscal) {
      state.dataNotaFiscal = dataNotafiscal
    },
    setPaymentMethodsPartial(state, partialPayment) {
      const companyId = state.room?.current_accommodation?.company?.id
      const normalizedPartialPayment = (partialPayment || []).map(payment => {
        if (payment.method === 'invoice' && !payment.origin && companyId) {
          return {
            ...payment,
            origin: 'company',
            id: payment.id || companyId,
          }
        }

        return payment
      })

      state.paymentMethodsPartial = normalizedPartialPayment

      const hasPartialInvoice = normalizedPartialPayment.some(payment => payment.method === 'invoice')
      if (hasPartialInvoice) {
        state.paymentMethods = state.paymentMethods.filter(payment => !(payment.method === 'invoice' && payment.auto_billing))
      }
    },
    setOriginPartialPayment(state, originPartialPayment) {
      state.originPartialPayment = originPartialPayment

      if (originPartialPayment !== 'checkout') {
        state.paymentMethods = state.paymentMethods.filter(payment => !payment.auto_billing)
        state.autoBillingApplied = false
      }
    },

    // PAGAMENTO SEPARADO

    addItemResponsible(state, item) {
      const index = state.checkout.indexOf(item)

      if (item.checked) {
        state.checkout[index].responsible = state.selectResponsible
      } else {
        delete state.checkout[index].responsible
      }

      // gabiarra pra forca a reacao do vue
      const { checkout } = state
      state.checkout = []
      state.checkout = checkout
    },

    setSelectecResposible(state, responsible) {
      state.selectResponsible = responsible
    },
    clearResponsible(state) {
      state.selectResponsible = {}
    },
    addPaymentMethodSeparate(state, payment) {
      payment.origin = state.selectResponsible.origin
      payment.id = state.selectResponsible.id
      state.paymentMethods.push(payment) // adicona pagamento
    },
    removePaymentMethodSeparate(state, payment) {
      state.paymentMethods.splice(state.paymentMethods.indexOf(payment), 1)
    },

    // END PAGAMENTO SEPARADO

    setMethodPayment(state, paymentMethods = []) {
      state.paymentMethods = paymentMethods
    },

    setAdditional(state, addicional) {
      state.additional = addicional
    },
    setDiscount(state, discount) {
      state.discount = discount
    },
    setRoom(state, room) {
      const currentAccommodationId = state.room?.current_accommodation?.id
      const nextAccommodationId = room?.current_accommodation?.id

      if (currentAccommodationId !== nextAccommodationId) {
        state.dataNotaFiscal = {}
      }

      state.room = room
      applyAutoBillingInvoice(state)
    },
    cancelSale(state) {
      state.customer = null
      state.checkout = []
      state.gratuities = []
      state.tax = 0
      state.stage = 'new_sale'
      state.paymentMethods = []
      state.sale_id = null
      state.showCheckout = true
      state.autoBillingApplied = false
    },
    setShowCheckout(state, showCheckout) {
      state.showCheckout = showCheckout
    },
    setNote(state, note) {
      state.note = note
    },

    setPaymentMethod(state, paymentMethods) {
      state.paymentMethods = paymentMethods
    },

    addPaymentMethod(state, payment) {
      if (payment.clearPayments) { // limpa todos os pagamnetos
        state.paymentMethods = []
      }

      // Parte comentada abaixo é para remover o pagamento do mesmo tipo
      /*
      const paymentMethod = state.paymentMethods.find((item)=>{
        return payment.method == item.method
      })

      if(paymentMethod != undefined){
        state.paymentMethods.splice(state.paymentMethods.indexOf(paymentMethod), 1)  // apaga o method de pagamento
      }
      */
      state.paymentMethods.push(payment) // adicona pagamento
    },

    removePaymentMethod(state, payment) {
      state.paymentMethods.splice(state.paymentMethods.indexOf(payment), 1)
    },
    setCheckout(state, checkout) {
      state.checkout = []// gabiarra pra força reacao do vue
      state.checkout = checkout
      applyAutoBillingInvoice(state)
    },
    addCheckout(state, item) {
      const search_item = state.checkout.find(search_item => (search_item.id == item.id && search_item.origin_checkout == item.origin_checkout))
      if (search_item == undefined) {
        item.qtd_checkout = 1
        state.checkout.push(item)
      } else {
        const index = state.checkout.indexOf(search_item)
        state.checkout[index].qtd_checkout += 1
      }

      if (state.customer == null) {
        state.customer = { id: 1, name: 'Cliente Padrão' }
      }
      state.showCheckout = true

      // gambiarra para força a a reacao do vue
      const { checkout } = state
      state.checkout = null
      state.checkout = checkout
    },

    setStage(state, stage) {
      state.stage = stage
    },

    clearCheckoutSeparete(state) {
      state.selectResponsible = {}
      state.paymentMethods = []
      state.note = ''
      state.additional = { value: 0, additional_obs: '' },
      state.discount = { value: 0, discount_obs: '' }

      // retira atribuicao de responsavel
      state.checkout.forEach(item => {
        delete item.responsible
      })
      state.separate_payment = false
      state.autoBillingApplied = false
    },

    clearCheckout(state) {
      state.selectResponsible = {}
      state.room = {}
      state.checkout = []
      state.stage = 'payment'
      state.paymentMethods = []
      state.paymentMethodsPartial = []
      state.note = ''
      state.dataNotaFiscal = {}
      state.additional = { value: 0, additional_obs: '' }
      state.discount = { value: 0, discount_obs: '' }
      state.separate_payment = false
      state.autoBillingApplied = false
    },
  },

  actions: {

    getUser() {
      return axios.get('helper/get_user')
    },

    cashsOpen() {
      return axios.get('helper/get_opened_cashs')
    },

    storeSale({ state, getters }) {
      const paymentSeparate = state.checkout.some(item => item.responsible)

      const sale = {
        room_id: state.room.id,
        accommodation_id: state.room.current_accommodation.id,
        checkout: state.checkout,
        dataNotaFiscal: state.dataNotaFiscal,
        paymentMethods: state.paymentMethods,
        subtotal: getters.getSubtotal,
        tax: getters.getTaxation,
        discount: state.discount,
        additional: state.additional,
        total_receive: getters.getPaymentMethodsTotal,
        change: getters.getChange,
        note: state.note,
        separate_payment: paymentSeparate,
      }

      return axios.post(`${route}/checkout`, sale) // {responseType: 'blob'}
    },
    transferToGroupAccount({ state, getters }, payload) {
      // payload: { groupAccountId, checkout?, advance_payments? } — checkout is the subset kept on the master
      const { groupAccountId, ...selection } = typeof payload === 'object' ? payload : { groupAccountId: payload }
      const sale = {
        room_id: state.room.id,
        accommodation_id: state.room.current_accommodation.id,
        checkout: selection.checkout || state.checkout,
        advance_payments: selection.advance_payments,
        leftover_checkout: selection.leftover_checkout
          ? { ...selection.leftover_checkout, dataNotaFiscal: selection.leftover_checkout.dataNotaFiscal || state.dataNotaFiscal, note: state.note }
          : undefined,
        dataNotaFiscal: state.dataNotaFiscal,
        paymentMethods: state.paymentMethods.map((method, index) => ({
          ...method,
          destination: (selection.new_payment_destinations || [])[index] || 'master',
        })),
        discount: state.discount,
        additional: state.additional,
        subtotal: getters.getSubtotal,
        tax: getters.getTaxation,
        total_receive: getters.getPaymentMethodsTotal,
        note: state.note,
      }

      return axios.post(`${route}/checkout/group_account/${groupAccountId}`, sale)
    },
    storeSaleOutstanding({ state, getters }) {
      const paymentSeparate = state.checkout.some(item => item.responsible)

      const sale = {
        room_id: state.room.id,
        accommodation_id: state.room.current_accommodation.id,
        checkout: state.checkout,
        dataNotaFiscal: state.dataNotaFiscal,
        paymentMethods: state.paymentMethods,
        subtotal: getters.getSubtotal,
        tax: getters.getTaxation,
        discount: state.discount,
        additional: state.additional,
        total_receive: getters.getPaymentMethodsTotal,
        change: getters.getChange,
        note: state.note,
        separate_payment: paymentSeparate,
      }

      return axios.post(`${route}/checkout_outstanding`, sale) // {responseType: 'blob'}
    },

    exportPdfCheckout({ state }) {
      const data = {
        accommodation_id: state.room.current_accommodation.id,
      }

      return axios.post(`${route}/export_pdf_checkout`, data, { responseType: 'blob' }) //
    },

    // SALVA ANEXO
    storeAttach({ state }, attach) {
      const formData = new FormData()
      if (state.originPartialPayment == 'reserve') {
        formData.append('reserve_id', state.room.current_accommodation.id)
      } else if (state.originPartialPayment == 'checkout') {
        formData.append('accommodation_id', state.room.current_accommodation.id)
      }

      formData.append('attach', attach.attach[0])
      formData.append('attach_obs', attach.attach_obs)

      return axios.post(`${route}/attach_checkout`, formData, { 'Content-Type': 'multipart/form-data' }) //
    },

    // BAIXAR ANEXO
    getDownloadAttach(context, attachId) {
      return axios.get(`${route}/attach_checkout/${attachId}`, { responseType: 'blob' })
    },

    // PAGAMENTO PARCIAL
    storePartialPayment({ state, getters }) {
      const partialPayment = {
        room_id: state.room.id,
        accommodation_id: state.room.current_accommodation.id,
        checkout: state.checkout,
        paymentMethods: state.paymentMethods,
        subtotal: getters.getSubtotal,
        tax: getters.getTaxation,
        discount: state.discount,
        additional: state.additional,
        total_receive: getters.getPaymentMethodsTotal,
        change: getters.getChange,
        note: state.note,
      }

      return axios.post(`${route}/partial_payment`, partialPayment)
    },

    // CARREGA OS PAGAMNETOS PARCIAS
    getPartialPayment({ state }) {
      const accommodation_id = state.room.current_accommodation.id

      return axios.get(`${route}/get_partial_payment/${accommodation_id}`)
    },

    // REMOVE PAGAMENTO PARCIAL
    destroyPaymentPartial(context, { justify, payment_item_id }) {
      return axios.post(`${route}/remove_partial_payment/${payment_item_id}`, { justify })
    },

    // SALVA PAGAMENTO PARCIAL NA RESERVA
    storeResevePartialPayment({ state, getters }, data) {
      const reserve_id = state.room.current_accommodation.id

      const partialPayment = {
        room_id: state.room.id,
        reserve_id: state.room.current_accommodation.id,
        checkout: state.checkout,
        paymentMethods: state.paymentMethods,
        subtotal: getters.getSubtotal,
        tax: getters.getTaxation,
        discount: state.discount,
        additional: state.additional,
        total_receive: getters.getPaymentMethodsTotal,
        change: getters.getChange,
        note: state.note,
        cash_id: data.cash_id,
      }

      return axios.post(`reserve/partial_payment/${reserve_id}`, partialPayment)
    },

    // CARREGA OS PAGAMNETOS ADIATADO NA RESERVA
    getResevePartialPayment({ state }) {
      const reserve_id = state.room.current_accommodation.id

      return axios.get(`reserve/get_partial_payment/${reserve_id}`)
    },

  },

}
